/**
 * Date arithmetic, mirroring `reminded.domain.dates` in Python.
 *
 * The one hazard here is timezone drift. PocketBase stores a date-only value as
 * UTC midnight ("2026-06-01 00:00:00.000Z"). Passing that to `new Date()` and
 * reading `getDate()` in a negative-offset timezone gives the *previous* day —
 * a passport would appear to expire a day early. So every date-only value is
 * handled as a plain year/month/day triple at local midnight, never as an
 * instant, and comparisons are done on whole days.
 */

/** Parse a PocketBase date-only field into local midnight of that calendar day. */
export function parseDateOnly(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

/** Serialise a Date to the "YYYY-MM-DD" form PocketBase accepts for a date field. */
export function toDateOnlyString(value: Date): string {
  const y = value.getFullYear()
  const m = String(value.getMonth() + 1).padStart(2, '0')
  const d = String(value.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Local midnight today — the reference point for every "days remaining". */
export function today(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

/**
 * Whole days between two local-midnight dates.
 *
 * Uses UTC epoch days rather than dividing a millisecond difference, so a DST
 * transition between the two dates cannot produce 0.958 days and round wrong.
 */
export function daysBetween(from: Date, to: Date): number {
  const utcFrom = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
  const utcTo = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((utcTo - utcFrom) / 86_400_000)
}

/** Format for display, e.g. "12 Mar 2026". */
export function formatDate(date: Date, locale = 'en-GB'): string {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Human phrasing for a deadline: "in 7 days", "today", "3 days ago". */
export function describeDays(days: number): string {
  if (days < 0) {
    const n = Math.abs(days)
    return `${n} day${n === 1 ? '' : 's'} ago`
  }
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 45) return `in ${days} days`
  const months = Math.round(days / 30)
  if (months < 18) return `in ${months} months`
  const years = Math.round(days / 365)
  return `in ${years} year${years === 1 ? '' : 's'}`
}

/** The IANA timezone the browser is in, for the settings default. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London'
  } catch {
    return 'Europe/London'
  }
}
